import { captureException } from '@sentry/react';
import { useQuery } from '@tanstack/react-query';
import { overlay } from 'overlay-kit';

import Button from '@/components/Button/Button';
import Spinner from '@/components/Common/Spinner';
import { modalContext } from '@/components/Modal/Modal';
import Typo from '@/components/Typography/Typo';
import { IMPORT_STATE } from '@/entities/(integration)/integrations';
import {
  type QualtricsInfo,
  qualtricsInfoQueryOptions,
  useDeleteQualtricsMutation,
  useImportQualtricsFeedbackMutation,
} from '@/entities/(integration)/qualtrics';
import { Dialog, DialogContent } from '@/shared/ui/dialog';
import useFeedbackSourceStore from '@/stores/Feedback/FeedbackSourceStore';
import { toast } from '@/stores/pure/ToastStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';

import IntegrationImportModal from '../../components/IntegrationImportModal';
import { ProviderContainer } from '../../components/ProviderContainer';
import type { IntegrationItemProps } from '../../types';
import QualtricsTokenModal from './QualtricsTokenModal';

export const QualtricsItem = ({ isConnector }: IntegrationItemProps) => {
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const fetchFeedbackSources = useFeedbackSourceStore(
    (state) => state.fetchFeedbackSources,
  );

  const workspaceAlias = currWorkspace?.alias;
  if (!workspaceAlias) throw new Error('Workspace alias is required');

  const { data: workspaces = [] } = useQuery(
    qualtricsInfoQueryOptions(workspaceAlias, { enabled: !isConnector }),
  );
  const { mutate: disconnectMutation } =
    useDeleteQualtricsMutation(workspaceAlias);
  const { mutate: requestImportMutation } =
    useImportQualtricsFeedbackMutation(workspaceAlias);

  type ImportModalData =
    | { isNew: true }
    | { isNew: false; workspace: QualtricsInfo };

  const openImportModal = (data: ImportModalData) => {
    overlay.open(({ isOpen, close, unmount }) => (
      <Dialog
        open={isOpen}
        onOpenChange={() => close()}
        onExitComplete={unmount}
      >
        <DialogContent className="w-auto border-none bg-transparent shadow-none rounded-none">
          <modalContext.Provider value={{ closeModal: async () => close() }}>
            <IntegrationImportModal
              isNew={data.isNew}
              title="Import Survey Responses"
              subTitle="Qualtrics"
              subTitleList={data.isNew ? undefined : data.workspace.alias}
              historyList={data.isNew ? [] : data.workspace.history || []}
              onSubmitImport={(start_date, end_date) => {
                handleImport({ start_date, end_date });
              }}
            />
          </modalContext.Provider>
        </DialogContent>
      </Dialog>
    ));
  };

  const handleTokenSuccess = () => {
    fetchFeedbackSources(workspaceAlias);
    openImportModal({ isNew: true });
  };

  const handleConnect = () => {
    overlay.open(({ isOpen, close, unmount }) => (
      <Dialog
        open={isOpen}
        onOpenChange={() => close()}
        onExitComplete={unmount}
      >
        <DialogContent size="m">
          <QualtricsTokenModal onClose={close} onSuccess={handleTokenSuccess} />
        </DialogContent>
      </Dialog>
    ));
  };

  const handleDisconnect = (_info: QualtricsInfo) => {
    disconnectMutation(undefined, {
      onError: (error) => {
        toast('Failed to disconnect Qualtrics', 'fail');
        captureException(error);
      },
    });
  };

  const handleImport = (dateRange: NullableDateRangeT) => {
    requestImportMutation({ dateRange });
  };

  return (
    <ProviderContainer
      multiInfoEnabled={true}
      connected={workspaces.length > 0}
      source={SOURCE_TYPE.QUALTRICS}
      description="Import survey responses as feedback into Syncly."
      connectedDisplay={() => 'Qualtrics survey'}
      infos={workspaces ?? []}
      onDisconnect={handleDisconnect}
      onConnectClick={handleConnect}
      onAddInfoClick={handleConnect}
      additionalActions={(workspace: QualtricsInfo) =>
        workspace.import_state === IMPORT_STATE.IMPORTING ? (
          <div className="flex items-center gap-2">
            <Spinner />
            <Typo type="body2" className="text-primary">
              Importing
            </Typo>
          </div>
        ) : (
          <Button
            variant="outlined"
            onClick={() => openImportModal({ workspace, isNew: false })}
          >
            One-time import
          </Button>
        )
      }
    />
  );
};
